import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Crown, Coffee } from "lucide-react";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";

/**
 * Home-page widget: highlights the top scorer from yesterday's UTC day for the active
 * weekly challenge. Drives return engagement ("come beat them today") and gives users
 * a reason to use their one-shot-per-day calc.
 *
 * Hidden when there is no active challenge OR no submissions yesterday — falls back to
 * a soft "be the first today" call-to-action so the slot is never visually empty.
 */
export default function YesterdaysWinner() {
  const { lang } = useI18n();
  const isRTL = lang === "ar";

  const { data } = useQuery({
    queryKey: ["yesterdays-winner"],
    queryFn: endpoints.yesterdaysWinner,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // No active challenge ⇒ hide entirely
  if (!data || !data.challenge) return null;

  const ch = data.challenge;
  const w = data.winner;
  const title = isRTL ? ch.title_ar : ch.title_en || ch.title_ar;
  const initial = (w?.user_name || "?").trim().charAt(0).toUpperCase() || "?";

  return (
    <section
      className="my-12 px-4 sm:px-8 lg:px-12"
      data-testid="yesterdays-winner-section"
    >
      <div
        className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden border border-[#E6D9C9] bg-gradient-to-br from-[#FFF8F2] via-[#FBEFE0] to-[#F4E1C8] shadow-sm"
        dir={isRTL ? "rtl" : "ltr"}
      >
        {/* Decorative crown blur */}
        <div className="pointer-events-none absolute -top-8 -end-8 text-[#C86240]/8 select-none">
          <Crown className="w-44 h-44" strokeWidth={1} />
        </div>

        <div className="relative grid md:grid-cols-[auto,1fr,auto] gap-6 items-center p-6 sm:p-8">
          {/* Avatar / initial */}
          <div className="flex items-center justify-center">
            {w?.user_picture ? (
              <img
                src={w.user_picture}
                alt={w.user_name}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-[#C86240] shadow-md object-cover"
                data-testid="yw-avatar"
              />
            ) : (
              <div
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-[#C86240] bg-[#C86240]/10 grid place-items-center text-3xl font-bold text-[#C86240] shadow-md"
                data-testid="yw-avatar-fallback"
              >
                {w ? initial : <Coffee className="w-9 h-9" />}
              </div>
            )}
          </div>

          {/* Middle — copy */}
          <div className="text-center md:text-start">
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[#C86240] mb-1">
              <Trophy className="w-3.5 h-3.5" />
              {isRTL ? "بطل الأمس" : "Yesterday's Winner"}
            </div>

            {w ? (
              <>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#2A2421] leading-tight" data-testid="yw-name">
                  {w.user_name || (isRTL ? "مجهول" : "Anonymous")}
                </h3>
                <p className="mt-2 text-sm text-[#5C544F]" data-testid="yw-meta">
                  {isRTL
                    ? `${w.score} نقطة · استخلاص ${Number(w.ey_pct).toFixed(1)}٪ · ${title}`
                    : `${w.score} pts · ${Number(w.ey_pct).toFixed(1)}% EY · ${title}`}
                </p>
                {typeof data.total_yesterday === "number" && data.total_yesterday > 1 && (
                  <p className="mt-1 text-xs text-[#8B7E73]">
                    {isRTL
                      ? `تفوّق على ${data.total_yesterday - 1} مشارك آخر — جاي دورك تكسره اليوم.`
                      : `Beat ${data.total_yesterday - 1} others — your turn to break their record.`}
                  </p>
                )}
              </>
            ) : (
              <>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#2A2421] leading-tight">
                  {isRTL ? "كن الأول اليوم" : "Be the first today"}
                </h3>
                <p className="mt-2 text-sm text-[#5C544F]">
                  {isRTL
                    ? `لا أحد شارك أمس في تحدّي ${title}. سجّل أول نتيجة وكن بطل الغد.`
                    : `Nobody submitted yesterday for ${title}. Post the first score and become tomorrow's winner.`}
                </p>
              </>
            )}
          </div>

          {/* CTA */}
          <div className="flex md:flex-col gap-2 justify-center">
            <Link
              to="/calculator?tab=extraction"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C86240] hover:bg-[#A8502F] text-white text-sm font-medium px-5 py-2.5 transition-colors shadow-sm"
              data-testid="yw-cta-try"
            >
              <Coffee className="w-4 h-4" />
              {isRTL ? "جرّب الآن" : "Take a shot"}
            </Link>
            <Link
              to="/challenge"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#FFF8F2] text-[#2A2421] text-sm font-medium px-5 py-2.5 border border-[#E6D9C9] transition-colors"
              data-testid="yw-cta-board"
            >
              <Trophy className="w-4 h-4" />
              {isRTL ? "اللوحة" : "Leaderboard"}
            </Link>
          </div>
        </div>

        {/* Bottom rule with "one shot per day" reminder */}
        <div className="relative px-6 sm:px-8 pb-4 -mt-2">
          <p className="text-[11px] text-[#8B7E73] text-center md:text-start">
            {isRTL
              ? "محاولة واحدة فقط في اليوم — اختر إعداداتك بعناية ☕"
              : "One shot per day — calibrate carefully ☕"}
          </p>
        </div>
      </div>
    </section>
  );
}
